import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  GitBranch,
  Loader2,
  GitCompare,
} from 'lucide-react';
import {
  useApplyOptimizationRun,
  useOptimizationRun,
} from '@/hooks/optimization/useOptimizationRuns';
import { useShifts } from '@/hooks/optimization/useOptimizationSettings';
import { useEmployees } from '@/hooks/useApi';
import { MultiAgentReport } from './MultiAgentReport';
import { SolutionCalendarGrid } from './SolutionCalendarGrid';
import { WhatIfDialog } from './WhatIfDialog';
import { CompareRunsDialog } from './CompareRunsDialog';
import { APP_TIME_ZONE } from '@/utils/dateFormat';

interface RunDetailDrawerProps {
  runId: string;
  open: boolean;
  onClose: () => void;
  onCompare?: (otherRunId: string) => void;
}

function formatKzt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n);
}

/**
 * Резолвит UUID должностей в имена через employees, у которых эта позиция.
 * Если employees пусты или нет совпадений — возвращает «N должностей».
 */
function resolvePositionNames(
  positionIds: string[],
  employees: Array<{ position?: { id?: string; name?: string } }>,
): string {
  const names = new Set<string>();
  for (const id of positionIds) {
    const emp = employees.find((e) => e.position?.id === id);
    if (emp?.position?.name) names.add(emp.position.name);
  }
  if (names.size === 0) return `${positionIds.length} должностей`;
  const arr = Array.from(names);
  if (arr.length <= 3) return arr.join(', ');
  return `${arr.slice(0, 3).join(', ')} +${arr.length - 3}`;
}

const MetricBox: React.FC<{
  label: string;
  value: string;
  tone?: 'default' | 'good' | 'warn';
}> = ({ label, value, tone = 'default' }) => {
  const valueColor =
    tone === 'good'
      ? 'text-green-700'
      : tone === 'warn'
      ? 'text-orange-700'
      : 'text-gray-900';
  return (
    <div className="border rounded-md p-3 bg-gray-50">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${valueColor}`}>{value}</div>
    </div>
  );
};

export const RunDetailDrawer: React.FC<RunDetailDrawerProps> = ({
  runId,
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const { data: run, isLoading } = useOptimizationRun(runId, {
    enablePolling: true,
    waitForAgentsReport: true,
  });
  const { data: shifts } = useShifts(run?.location_id ?? null);
  // Employees: у /employees нет фильтра по локации (location_id отвергается
  // ValidationPipe), поэтому берём всех сотрудников тенанта — для резолва имён
  // по employee_id из назначений этого достаточно.
  const { data: empResp } = useEmployees({ limit: 1000 }, { enabled: !!run });
  const employees = useMemo<
    Array<{
      id: string;
      first_name?: string;
      last_name?: string;
      middle_name?: string;
      position?: { id?: string; name?: string };
    }>
  >(() => {
    const d = (empResp as { data?: unknown[] })?.data;
    if (!d) return [];
    return d as Array<{
      id: string;
      first_name?: string;
      last_name?: string;
      middle_name?: string;
      position?: { id?: string; name?: string };
    }>;
  }, [empResp]);

  const apply = useApplyOptimizationRun();
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [applyError, setApplyError] = useState<string>('');

  const assignments = run?.solution?.assignments ?? [];
  const metrics = run?.solution?.metrics ?? {};

  const handleApply = async () => {
    if (!run) return;
    setApplyError('');
    try {
      await apply.mutateAsync(run.id);
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : 'Ошибка применения');
    }
  };

  const employeeInfo = useMemo(() => {
    const m = new Map<string, { name: string; position: string | null }>();
    for (const e of employees) {
      const ln = e.last_name ?? '';
      const fn = e.first_name ? ` ${e.first_name}` : '';
      const mn = e.middle_name ? ` ${e.middle_name}` : '';
      const full = `${ln}${fn}${mn}`.trim() || e.id.slice(0, 8);
      m.set(e.id, {
        name: full,
        position: e.position?.name ?? null,
      });
    }
    return m;
  }, [employees]);

  const shiftByCode = useMemo(() => {
    const m = new Map<
      string,
      { code: string; name: string; start_time: string; end_time: string }
    >();
    for (const s of shifts ?? []) {
      m.set(s.id, {
        code: s.code,
        name: s.name,
        start_time: s.start_time,
        end_time: s.end_time,
      });
    }
    return m;
  }, [shifts]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-6xl">
        <DialogClose onClose={onClose} />
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              Запуск оптимизации
              {run?.parent_run_id && (
                <Badge
                  variant="outline"
                  className="text-[10px] bg-purple-50 border-purple-200 text-purple-700"
                >
                  what-if
                </Badge>
              )}
              {run?.applied_as_request_id && (
                <Badge
                  variant="outline"
                  className="text-[10px] bg-blue-50 border-blue-200 text-blue-700"
                >
                  применён
                </Badge>
              )}
              {run?.position_ids && run.position_ids.length > 0 && (
                <Badge
                  variant="outline"
                  className="text-[10px] bg-amber-50 border-amber-200 text-amber-800"
                >
                  per-position ({run.position_ids.length})
                </Badge>
              )}
            </span>
          </DialogTitle>
        </DialogHeader>

        {isLoading || !run ? (
          <div className="py-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span>
                Период: <b>{run.period_start}</b> → <b>{run.period_end}</b>
              </span>
              <span>Сценарий: {run.scenario_code}</span>
              <span>Solver: {run.solver_status ?? '—'}</span>
              <span>
                Время: {run.solve_time_ms ? `${run.solve_time_ms} мс` : '—'}
              </span>
              <span>
                Создан:{' '}
                {new Date(run.created_at).toLocaleString('ru-RU', {
                  timeZone: APP_TIME_ZONE,
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </span>
              {run.position_ids && run.position_ids.length > 0 && (
                <span>
                  Должности:{' '}
                  <b>{resolvePositionNames(run.position_ids, employees)}</b>
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricBox
                label="ФОТ"
                value={
                  metrics.total_fot ? `${formatKzt(metrics.total_fot)} ₸` : '—'
                }
              />
              <MetricBox
                label="Покрытие"
                value={
                  metrics.coverage_score !== undefined
                    ? `${(metrics.coverage_score * 100).toFixed(0)}%`
                    : '—'
                }
                tone={
                  metrics.coverage_score !== undefined &&
                  metrics.coverage_score >= 0.9
                    ? 'good'
                    : 'warn'
                }
              />
              <MetricBox
                label="Недокрытие"
                value={
                  metrics.understaffed_hours !== undefined
                    ? `${metrics.understaffed_hours} ч`
                    : '—'
                }
                tone={metrics.understaffed_hours ? 'warn' : 'good'}
              />
              <MetricBox
                label="Переработки"
                value={
                  metrics.overtime_hours !== undefined
                    ? `${metrics.overtime_hours} ч`
                    : '—'
                }
                tone={metrics.overtime_hours ? 'warn' : 'good'}
              />
            </div>

            <MultiAgentReport run={run} />

            <SolutionCalendarGrid
              periodStart={run.period_start}
              periodEnd={run.period_end}
              assignments={assignments}
              employeeInfo={employeeInfo}
              shiftByCode={shiftByCode}
            />

            {applyError && (
              <Alert variant="destructive">
                <AlertDescription>{applyError}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCompareOpen(true)}
                disabled={!run || run.status !== 'completed'}
              >
                <GitCompare className="h-4 w-4 mr-2" />
                Сравнить
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWhatIfOpen(true)}
                disabled={run.status !== 'completed'}
              >
                <GitBranch className="h-4 w-4 mr-2" />
                What-if
              </Button>
              {run.applied_as_request_id ? (
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => {
                    onClose();
                    navigate('/schedule-change-requests');
                  }}
                >
                  Открыть заявку
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={
                    apply.isPending ||
                    run.status !== 'completed' ||
                    assignments.length === 0
                  }
                >
                  {apply.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Применить
                </Button>
              )}
            </div>
          </div>
        )}

        {run && whatIfOpen && (
          <WhatIfDialog
            parentRun={run}
            open={whatIfOpen}
            onClose={() => setWhatIfOpen(false)}
          />
        )}

        {run && compareOpen && (
          <CompareRunsDialog
            currentRun={run}
            open={compareOpen}
            onClose={() => setCompareOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
